import { useState, useCallback } from 'react';
import L from 'leaflet';
import { Customer } from '../../../../api/mobiApi/domain/domain.types';


interface Point {
  x: number;
  y: number;
  lat: number;
  lng: number;
}

export const usePolygonDrawing = (
  mapInstance: L.Map | null,
  customers: Customer[],
  onCustomersSelect?: (customers: Customer[]) => void
) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [polygonPoints, setPolygonPoints] = useState<Point[]>([]);
  const [currentMousePoint, setCurrentMousePoint] = useState<Point | null>(null);

  // Start polygon drawing
  const startDrawing = useCallback(() => {
    setIsDrawing(true);
    setPolygonPoints([]);
    setCurrentMousePoint(null);
  }, []);

  // Add a point to polygon
  const addPoint = useCallback((e: React.MouseEvent) => {
    if (!mapInstance || !isDrawing) return;

    const mapContainer = mapInstance.getContainer();
    const mapRect = mapContainer.getBoundingClientRect();

    // Convert client coordinates to container-relative coordinates
    const containerX = e.clientX - mapRect.left;
    const containerY = e.clientY - mapRect.top;

    // Convert to lat/lng
    const latLng = mapInstance.containerPointToLatLng([containerX, containerY]);

    const newPoint: Point = {
      x: e.clientX,
      y: e.clientY,
      lat: latLng.lat,
      lng: latLng.lng
    };

    setPolygonPoints(prev => [...prev, newPoint]);
    console.log('📍 Point added:', newPoint);
  }, [mapInstance, isDrawing]);

  // Update current mouse position for preview
  const updateMousePosition = useCallback((e: React.MouseEvent) => {
    if (!mapInstance || !isDrawing) return;

    const mapContainer = mapInstance.getContainer();
    const mapRect = mapContainer.getBoundingClientRect();

    const containerX = e.clientX - mapRect.left;
    const containerY = e.clientY - mapRect.top;

    const latLng = mapInstance.containerPointToLatLng([containerX, containerY]);

    setCurrentMousePoint({
      x: e.clientX,
      y: e.clientY,
      lat: latLng.lat,
      lng: latLng.lng
    });
  }, [mapInstance, isDrawing]);

  // Finish polygon and select customers
  const finishDrawing = useCallback(() => {
    if (!mapInstance || polygonPoints.length < 3) {
      console.warn('⚠️ Polygon needs at least 3 points');
      return;
    }

    console.log('🔍 Finishing polygon with', polygonPoints.length, 'points');

    // Create polygon coordinates for point-in-polygon test
    const polygonCoords = polygonPoints.map(p => [p.lat, p.lng]);

    // Find customers within polygon
    const selectedCustomers = customers.filter(customer => {
      const point: [number, number] = [customer.lat, customer.lon];
      const isInside = isPointInPolygon(point, polygonCoords);

      if (isInside) {
        console.log('✅ Selected customer:', customer.name, { lat: customer.lat, lon: customer.lon });
      }

      return isInside;
    });

    console.log(`📊 Selected ${selectedCustomers.length} customers`);

    if (onCustomersSelect) {
      onCustomersSelect(selectedCustomers);
    }

    // Reset drawing state
    setIsDrawing(false);
    setPolygonPoints([]);
    setCurrentMousePoint(null);
  }, [mapInstance, polygonPoints, customers, onCustomersSelect]);

  // Cancel drawing
  const cancelDrawing = useCallback(() => {
    setIsDrawing(false);
    setPolygonPoints([]);
    setCurrentMousePoint(null);
  }, []);

  // Remove last point
  const removeLastPoint = useCallback(() => {
    setPolygonPoints(prev => prev.slice(0, -1));
  }, []);

  return {
    isDrawing,
    polygonPoints,
    currentMousePoint,
    startDrawing,
    addPoint,
    updateMousePosition,
    finishDrawing,
    cancelDrawing,
    removeLastPoint
  };
};

// Point in polygon algorithm (Ray Casting)
function isPointInPolygon(point: [number, number], polygon: number[][]): boolean {
  const [x, y] = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];

    const intersect = ((yi > y) !== (yj > y)) &&
      (x < (xj - xi) * (y - yi) / (yj - yi) + xi);

    if (intersect) inside = !inside;
  }

  return inside;
}
